import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { MailOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import axios from '../../utils/axios';

const ForgotPassword = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [email, setEmail] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [expiryMinutes, setExpiryMinutes] = useState(10);
  const countdownTimerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  const startCountdown = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    setCountdown(60);
    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendCodeMutation = useMutation(
    async (values) => {
      const response = await axios.post('/auth/forgot-password', values);
      return response.data;
    },
    {
      onSuccess: (data) => {
        const msg =
          data?.message ||
          'If that email is registered, you will receive a verification code. Please check your inbox and spam folder.';
        message.success(msg);
        setEmail(form.getFieldValue('email'));
        if (typeof data?.expiryMinutes === 'number') {
          setExpiryMinutes(data.expiryMinutes);
        }
        setCurrentStep(1);
        startCountdown();
      },
      onError: (error) => {
        const payload = error.response?.data;
        const status = error.response?.status;
        let errorMessage =
          payload?.message || 'Failed to send verification code. Please try again later.';
        if (status === 429 && payload?.retryAfter) {
          const m = Math.ceil(Number(payload.retryAfter) / 60);
          errorMessage = `${errorMessage} (retry in ~${m || 1} min)`;
        }
        message.error(errorMessage);
      },
    }
  );

  const verifyCodeMutation = useMutation(
    async (values) => {
      const response = await axios.post('/auth/verify-reset-code', {
        email: email,
        code: values.code,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Verification code confirmed. Please set your new password.');
        setCurrentStep(2);
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message || 'Invalid or expired verification code.';
        message.error(errorMessage);
      },
    }
  );

  const resetPasswordMutation = useMutation(
    async (values) => {
      const response = await axios.post('/auth/reset-password', {
        email: email,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        message.success(
          data?.message || 'Password reset. Please login with your new password.'
        );
        navigate('/login');
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message || 'Failed to reset password. Please try again.';
        message.error(errorMessage);
      },
    }
  );

  const handleSendCode = (values) => {
    sendCodeMutation.mutate(values);
  };

  const handleVerifyCode = (values) => {
    verifyCodeMutation.mutate(values);
  };

  const handleResetPassword = (values) => {
    resetPasswordMutation.mutate(values);
  };

  const handleResendCode = () => {
    sendCodeMutation.mutate({ email });
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 0:
        return {
          title: 'Forgot Password',
          subtitle: 'Enter the email used for registration. We will send a 6-digit verification code.',
          form: (
            <Form
              form={form}
              name="sendCode"
              onFinish={handleSendCode}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Invalid email format' },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Registered email"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={sendCodeMutation.isLoading}
                >
                  Send verification code
                </Button>
              </Form.Item>
            </Form>
          ),
        };

      case 1:
        return {
          title: 'Enter Verification Code',
          subtitle: `We've sent a code to ${email} (valid for ${expiryMinutes} min; check spam if needed).`,
          form: (
            <Form
              name="verifyCode"
              onFinish={handleVerifyCode}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="code"
                label="Verification Code"
                rules={[
                  { required: true, message: 'Please enter the verification code.' },
                  { len: 6, message: 'Code must be 6 digits.' },
                  {
                    pattern: /^\d{6}$/,
                    message: 'Please enter a valid 6-digit code.',
                  },
                ]}
              >
                <Input
                  prefix={<SafetyOutlined />}
                  placeholder="6-digit code"
                  maxLength={6}
                  style={{
                    textAlign: 'center',
                    fontSize: '18px',
                    letterSpacing: '4px',
                  }}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={verifyCodeMutation.isLoading}
                >
                  Verify
                </Button>
              </Form.Item>

              <div style={{ textAlign: 'center' }}>
                {countdown > 0 ? (
                  <span style={{ color: '#666' }}>
                    Resend in {countdown}s
                  </span>
                ) : (
                  <Button
                    type="link"
                    onClick={handleResendCode}
                    loading={sendCodeMutation.isLoading}
                  >
                    Resend verification code
                  </Button>
                )}
              </div>
            </Form>
          ),
        };

      case 2:
        return {
          title: 'Set New Password',
          subtitle: 'Set your new login password (at least 6 characters)',
          form: (
            <Form
              name="resetPassword"
              onFinish={handleResetPassword}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="password"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter your new password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="New password"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please enter your new password again' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('The two passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter new password again"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={resetPasswordMutation.isLoading}
                >
                  Confirm Reset Password
                </Button>
              </Form.Item>
            </Form>
          ),
        };

      default:
        return null;
    }
  };

  const stepContent = getStepContent();

  if (!stepContent) {
    return null;
  }

  return (
    <Card className="login-form" style={{ maxWidth: 450, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <img
          src="/images/vouchain-logo.png"
          alt="Vouchain Logo"
          style={{ height: 48, marginBottom: 12 }}
        />
        <h1
          className="login-title"
          style={{ margin: '2px 0', fontSize: '24px', fontWeight: 800 }}
        >
          {stepContent.title}
        </h1>
        <p
          className="login-subtitle"
          style={{ margin: 0, fontSize: '14px', color: '#666' }}
        >
          {stepContent.subtitle}
        </p>
      </div>

      {stepContent.form}

      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <Link to="/login">Back to Login</Link>
      </div>
    </Card>
  );
};

export default ForgotPassword;
